// frontend/src/App.tsx
import { useState, lazy, Suspense, type ComponentType } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoadingScreen } from "./components/LoadingScreen";
import { PublicShell } from "./components/PublicShell";
import { useAuth } from "./lib/auth";
import { isMda, isNbs, canManageUsers, canEnterData } from "./lib/roles";
const lz = (factory: () => Promise<any>, key: string) =>
  lazy(() => factory().then((m) => ({ default: m[key] }))) as unknown as ComponentType<any>;
const Login = lz(() => import("./pages/auth/Login"), "Login");
const ForgotPassword = lz(() => import("./pages/auth/ForgotPassword"), "ForgotPassword");
const ResetPassword = lz(() => import("./pages/auth/ResetPassword"), "ResetPassword");
const PublicDashboard = lz(() => import("./pages/public/PublicDashboard"), "PublicDashboard");
const Page = lz(() => import("./pages/public/Page"), "Page");  
const Microdata = lz(() => import("./pages/data/Microdata"), "Microdata");
const AgencyDashboard = lz(() => import("./pages/agency/AgencyDashboard"), "AgencyDashboard");
const DataEntry = lz(() => import("./pages/agency/DataEntry"), "DataEntry");
const Submissions = lz(() => import("./pages/agency/Submissions"), "Submissions");
const NbsDashboard = lz(() => import("./pages/nbs/NbsDashboard"), "NbsDashboard");
const ValidationQueue = lz(() => import("./pages/nbs/ValidationQueue"), "ValidationQueue");
const Denominators = lz(() => import("./pages/nbs/Denominators"), "Denominators");
const AllSubmissions = lz(() => import("./pages/nbs/AllSubmissions"), "AllSubmissions");
const SubmissionView = lz(() => import("./pages/nbs/SubmissionView"), "SubmissionView");
const Publishing = lz(() => import("./pages/nbs/Publishing"), "Publishing");
const AuditLog = lz(() => import("./pages/nbs/AuditLog"), "AuditLog");
const Agencies = lz(() => import("./pages/admin/Admin"), "Agencies");
const Cycles = lz(() => import("./pages/admin/Admin"), "Cycles");
const Users = lz(() => import("./pages/admin/Admin"), "Users");
function Home({ cycle, setCycle }: { cycle: number; setCycle: (y: number) => void }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user
    ? <AppShell cycle={cycle} setCycle={setCycle}><PublicDashboard cycle={cycle} /></AppShell>
    : <PublicShell cycle={cycle} setCycle={setCycle}><PublicDashboard cycle={cycle} /></PublicShell>;
}
function PageWrapper({ cycle, setCycle }: { cycle: number; setCycle: (y: number) => void }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user
    ? <AppShell cycle={cycle} setCycle={setCycle}><Page /></AppShell>
    : <PublicShell cycle={cycle} setCycle={setCycle}><Page /></PublicShell>;
}
export default function App() {
  const [cycle, setCycle] = useState<number>(2025);
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<Home cycle={cycle} setCycle={setCycle} />} />
        <Route path="/page/:slug" element={<PageWrapper cycle={cycle} setCycle={setCycle} />} />
        <Route element={<ProtectedRoute><AppShell cycle={cycle} setCycle={setCycle} /></ProtectedRoute>}>
          <Route path="microdata" element={<Microdata />} />
          <Route path="agency" element={<ProtectedRoute allow={isMda}><AgencyDashboard cycle={cycle} /></ProtectedRoute>} />
          <Route path="agency/entry" element={<ProtectedRoute allow={canEnterData}><DataEntry cycle={cycle} /></ProtectedRoute>} />
          <Route path="agency/submissions" element={<ProtectedRoute allow={isMda}><Submissions /></ProtectedRoute>} />
          <Route path="nbs" element={<ProtectedRoute allow={isNbs}><NbsDashboard cycle={cycle} /></ProtectedRoute>} />
          <Route path="nbs/submissions" element={<ProtectedRoute allow={isNbs}><AllSubmissions /></ProtectedRoute>} />
          <Route path="nbs/submissions/:id" element={<ProtectedRoute allow={isNbs}><SubmissionView /></ProtectedRoute>} />
          <Route path="nbs/validation" element={<ProtectedRoute allow={isNbs}><ValidationQueue /></ProtectedRoute>} />
          <Route path="nbs/denominators" element={<ProtectedRoute allow={isNbs}><Denominators cycle={cycle} /></ProtectedRoute>} />
          <Route path="nbs/publish" element={<ProtectedRoute allow={isNbs}><Publishing /></ProtectedRoute>} />
          <Route path="nbs/audit" element={<ProtectedRoute allow={isNbs}><AuditLog /></ProtectedRoute>} />
          <Route path="admin/users" element={<ProtectedRoute allow={canManageUsers}><Users /></ProtectedRoute>} />
          <Route path="admin/agencies" element={<ProtectedRoute allow={canManageUsers}><Agencies /></ProtectedRoute>} />
          <Route path="admin/cycles" element={<ProtectedRoute allow={canManageUsers}><Cycles /></ProtectedRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
